"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, ShieldAlert, SquareCheck as CheckSquare2, Wrench, CircleQuestionMark as HelpCircle, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardTitle, CardContent } from "@/components/ui/card";
const bullItems = [{
  label: "Controller",
  detail: "Check the type of controller and which functions it offers."
}, {
  label: "Documented motion functions",
  detail: "Review the motion functions actually documented for the exact machine."
}, {
  label: "Visible condition",
  detail: "Review cosmetic wear on the body, horn integrity, and saddle mountings."
}, {
  label: "Power requirements",
  detail: "Confirm the power requirements documented for the exact machine."
}, {
  label: "Known repairs",
  detail: "Ask for any maintenance or repair records where applicable."
}, {
  label: "Parts availability",
  detail: "Confirm whether replacement parts are available for this model."
}];
const inflatableItems = [{
  label: "Dimensions",
  detail: "Measure outer footprint to ensure proper perimeter clearance for riders."
}, {
  label: "Visible condition",
  detail: "Review visible wear, discoloration, and overall condition."
}, {
  label: "Previous repairs",
  detail: "Ask about any known previous repairs or patches, where genuinely documented."
}, {
  label: "Center opening or bull interface",
  detail: "Inspect the collar diameter and anchoring around the pedestal."
}, {
  label: "Blower requirements",
  detail: "Confirm the blower requirements published for the exact inflatable."
}, {
  label: "Packed size",
  detail: "Evaluate storage volume and weight for transit."
}, {
  label: "Included accessories",
  detail: "Review which accessories are included with the arena."
}];
export default function Evaluate() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="evaluatebullandinflatable" data-nav="light" className="w-full bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold uppercase tracking-tight text-foreground mb-4">
            Compare the Mechanical Bull and Inflatable Separately
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            One of the most useful ways to compare these systems is to treat the mechanical equipment and inflatable arena as two major parts of the same package. Their condition, compatibility and replacement needs may be very different.
          </p>
        </div>

        {/* Two-Column Cards */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          {/* Left Column: The Mechanical Bull */}
          <motion.div variants={cardVariants}>
            <Card className="h-full rounded-none border border-border bg-card shadow-sm flex flex-col justify-between py-0 gap-0">
              <div>
                <div className="bg-muted/40 border-b border-border p-6 flex items-center gap-3">
                  <div className="p-2.5 bg-primary text-primary-foreground shrink-0">
                    <Cpu className="size-5" />
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-serif font-bold uppercase tracking-wide text-card-foreground">
                    The Mechanical Bull
                  </CardTitle>
                </div>

                <CardContent className="p-6 sm:p-8 space-y-6">
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    The mechanical section determines how the ride operates. Pay attention to the exact controller and machine information rather than assuming every powered bull behaves the same way.
                  </p>

                  <div className="space-y-3 pt-2">
                    <div className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground border-b border-border pb-2 flex items-center gap-2">
                      <Wrench className="size-3.5 text-primary" />
                      Key review points
                    </div>
                    <ul className="space-y-3 pt-1">
                      {bullItems.map((item, i) => <li key={item.label} data-index={i} className="flex items-start gap-3 p-2.5 bg-muted/20 border border-border/60">
                          <CheckSquare2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <span className="font-mono text-xs uppercase font-semibold text-foreground mr-2">
                              {item.label}:
                            </span>
                            <span className="text-muted-foreground text-xs sm:text-sm">
                              {item.detail}
                            </span>
                          </div>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>
              </div>
            </Card>
          </motion.div>

          {/* Right Column: The Inflatable Safety Arena */}
          <motion.div variants={cardVariants}>
            <Card className="h-full rounded-none border border-border bg-card shadow-sm flex flex-col justify-between py-0 gap-0">
              <div>
                <div className="bg-muted/40 border-b border-border p-6 flex items-center gap-3">
                  <div className="p-2.5 bg-secondary text-secondary-foreground shrink-0">
                    <ShieldAlert className="size-5" />
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-serif font-bold uppercase tracking-wide text-card-foreground">
                    The Inflatable Safety Arena
                  </CardTitle>
                </div>

                <CardContent className="p-6 sm:p-8 space-y-6">
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    The inflatable can wear differently from the mechanical equipment. A bull may still have useful service life while its inflatable needs repair or replacement, so evaluate the two separately.
                  </p>

                  <div className="space-y-3 pt-2">
                    <div className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground border-b border-border pb-2 flex items-center gap-2">
                      <Wrench className="size-3.5 text-secondary-foreground" />
                      Key review points
                    </div>
                    <ul className="space-y-3 pt-1">
                      {inflatableItems.map((item, i) => <li key={item.label} data-index={i} className="flex items-start gap-3 p-2.5 bg-muted/20 border border-border/60">
                          <CheckSquare2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <span className="font-mono text-xs uppercase font-semibold text-foreground mr-2">
                              {item.label}:
                            </span>
                            <span className="text-muted-foreground text-xs sm:text-sm">
                              {item.detail}
                            </span>
                          </div>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Closing Line & Action Banner */}
        <div className="border border-border bg-accent/40 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="p-2 bg-background border border-border shrink-0 mt-1 sm:mt-0">
              <HelpCircle className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-sm sm:text-base font-medium text-foreground leading-snug">
                This separation makes it easier to understand what you are paying for and what may eventually need replacement.
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 font-mono">
                Ask our team for full details on any model.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default" className="rounded-none font-mono text-xs uppercase tracking-wider px-5 w-full sm:w-auto">
              <Link to="/RequestQuotePage">
                <span>Request quote</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-none font-mono text-xs uppercase tracking-wider px-4 w-full sm:w-auto">
              <Link to="/MechanicalBullBuyingGuidePage">
                <span>View buying guide</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}